/* Capacitaciones Administrativas - vista del participante (TD-11629).
 *
 * - Escanear el QR que muestra el anfitrión (en su celular o compartiendo
 *   pantalla). El escaneo trae la declaración "Declaro haber participado..." y
 *   un ticket personal válido 10 min, así la persona puede leer y confirmar sin
 *   que el QR (rota cada 30 s) venza en el medio.
 * - Ver confirmaciones/evaluaciones pendientes (las responde el modal global).
 * - Historial de capacitaciones confirmadas con su puntaje.
 */
import { IonBadge, IonButton, IonContent, IonIcon, IonPage, useIonToast } from "@ionic/react";
import { CircularProgress } from "@material-ui/core";
import { calendarOutline, checkmarkCircle, personOutline, qrCodeOutline, schoolOutline } from "ionicons/icons";
import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import Header from "src/components/Header";
import CapacitacionAdmAPI, {
  CAPADM_PENDIENTES_CAMBIARON,
  EscaneoQR,
  MiCapacitacion,
  Pendientes,
  abrirPendientesCapAdm,
} from "src/services/capacitacionAdm";
import { colorPuntaje, formatFechaCorta, formatFechaHora, textoEvaluacionDesde } from "src/utils/capacitacionAdm";
import "./CapacitacionAdm.css";

const SCANNER_ID = "capadm-qr-scanner";

const CapacitacionAdmMis: React.FC = () => {
  const [present] = useIonToast();
  const [cargando, setCargando] = useState(true);
  const [mis, setMis] = useState<MiCapacitacion[]>([]);
  const [pendientes, setPendientes] = useState<Pendientes | null>(null);

  const [escaneando, setEscaneando] = useState(false);
  const [escaneo, setEscaneo] = useState<EscaneoQR | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const procesandoRef = useRef(false);

  const cargar = async () => {
    const [lista, pend] = await Promise.all([
      CapacitacionAdmAPI.mis().catch(() => [] as MiCapacitacion[]),
      CapacitacionAdmAPI.pendientes(),
    ]);
    setMis(lista);
    setPendientes(pend);
    setCargando(false);
  };

  useEffect(() => {
    cargar();
    // El modal global avisa cuando se respondió algo: refrescamos contadores.
    window.addEventListener(CAPADM_PENDIENTES_CAMBIARON, cargar);
    return () => {
      window.removeEventListener(CAPADM_PENDIENTES_CAMBIARON, cargar);
      detenerScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------------- Escáner QR -------------------------
  const detenerScanner = async () => {
    try {
      if (scannerRef.current) {
        const s = scannerRef.current;
        scannerRef.current = null;
        // @ts-ignore html5-qrcode expone getState() en runtime
        if (s.getState && s.getState() === 2) {
          await s.stop();
        }
        await s.clear();
      }
    } catch {
      // noop
    }
  };

  useEffect(() => {
    if (!escaneando) {
      detenerScanner();
      return;
    }
    // El div del scanner debe existir en el DOM antes de iniciar la cámara.
    const t = setTimeout(async () => {
      try {
        await detenerScanner();
        const scanner = new Html5Qrcode(SCANNER_ID, false);
        scannerRef.current = scanner;
        procesandoRef.current = false;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decodedText) => procesarQR(decodedText),
          () => {
            /* errores de frame no nos interesan */
          }
        );
      } catch {
        present({
          message: "No se pudo abrir la cámara. Revisá que la app tenga permiso para usarla.",
          duration: 4000,
          color: "danger",
        });
        setEscaneando(false);
      }
    }, 120);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [escaneando]);

  const procesarQR = async (qr: string) => {
    if (procesandoRef.current) return;
    procesandoRef.current = true;
    try {
      const res = await CapacitacionAdmAPI.escanearQR(qr);
      setEscaneo(res);
      setEscaneando(false);
    } catch (err: any) {
      present({
        message: err?.response?.data?.error || "Este QR no es de una capacitación",
        duration: 3000,
        color: "warning",
      });
      // Dejamos seguir escaneando (el QR del anfitrión rota solo).
      setTimeout(() => {
        procesandoRef.current = false;
      }, 1500);
    }
  };

  const confirmar = async () => {
    if (!escaneo) return;
    setConfirmando(true);
    try {
      const res = await CapacitacionAdmAPI.confirmarQR(escaneo.ticket);
      setEscaneo(null);
      await cargar();
      if (res.evaluacion_disponible) {
        present({ message: "¡Asistencia registrada!", duration: 2500, color: "success" });
        abrirPendientesCapAdm();
      } else {
        // Capacitación en curso: la evaluación llega al terminar.
        present({
          message: res.tiene_evaluacion
            ? `¡Asistencia registrada! ${textoEvaluacionDesde(res.fecha_fin)}`
            : "¡Asistencia registrada!",
          duration: 4500,
          color: "success",
        });
      }
    } catch (err: any) {
      present({
        message: err?.response?.data?.error || "No se pudo registrar la asistencia",
        duration: 4000,
        color: "danger",
      });
    } finally {
      setConfirmando(false);
    }
  };

  const nConf = pendientes?.confirmaciones.length || 0;
  const nEval = pendientes?.evaluaciones.length || 0;

  return (
    <IonPage>
      <Header titulo="Mis capacitaciones" />
      <IonContent id="capacitaciones-adm-mis">
        <div className="capadm-page capadm-scope campos-mui">
          {/* ---------- Registro por QR ---------- */}
          {escaneo ? (
            <div className="capadm-card">
              <p className="capadm-card-titulo" style={{ fontSize: 17 }}>{escaneo.capacitacion.tema}</p>
              <div className="capadm-meta">
                <span>
                  <IonIcon icon={calendarOutline} /> {formatFechaHora(escaneo.capacitacion.fecha_capacitacion)}
                </span>
                {escaneo.capacitacion.anfitrion_nombre && (
                  <span>
                    <IonIcon icon={personOutline} /> {escaneo.capacitacion.anfitrion_nombre}
                  </span>
                )}
              </div>
              {escaneo.ya_confirmado ? (
                <>
                  <div className="capadm-aviso info" style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center" }}>
                    <IonIcon icon={checkmarkCircle} style={{ color: "#2e7d32", fontSize: 20 }} />
                    Ya registraste tu asistencia a esta capacitación.
                  </div>
                  <IonButton expand="block" fill="outline" onClick={() => setEscaneo(null)}>
                    Volver
                  </IonButton>
                </>
              ) : (
                <>
                  <div className="capadm-declaracion">“{escaneo.texto_consentimiento}”</div>
                  <IonButton expand="block" onClick={confirmar} disabled={confirmando}>
                    {confirmando ? "Registrando..." : "Confirmo mi participación"}
                  </IonButton>
                  <IonButton expand="block" fill="clear" color="medium" onClick={() => setEscaneo(null)} disabled={confirmando}>
                    Cancelar
                  </IonButton>
                </>
              )}
            </div>
          ) : escaneando ? (
            <div className="capadm-card" style={{ textAlign: "center" }}>
              <p className="capadm-card-sub">Apuntá la cámara al QR que muestra el anfitrión.</p>
              <div id={SCANNER_ID} style={{ width: "100%", maxWidth: 400, margin: "0 auto", borderRadius: 8, overflow: "hidden" }} />
              <IonButton expand="block" fill="outline" onClick={() => setEscaneando(false)} style={{ marginTop: 12 }}>
                Cancelar
              </IonButton>
            </div>
          ) : (
            <div className="capadm-card" style={{ textAlign: "center" }}>
              <IonIcon icon={qrCodeOutline} style={{ fontSize: 48, color: "var(--ion-color-primary)" }} />
              <p className="capadm-card-titulo" style={{ marginTop: 6 }}>¿Estás en una capacitación?</p>
              <p className="capadm-card-sub">Escaneá el QR del anfitrión para registrar tu asistencia.</p>
              <IonButton expand="block" onClick={() => setEscaneando(true)}>
                Escanear QR
              </IonButton>
            </div>
          )}

          {/* ---------- Pendientes ---------- */}
          {nConf + nEval > 0 && (
            <div className="capadm-aviso pendiente">
              <div style={{ marginBottom: 8 }}>
                {nConf > 0 && (
                  <div>
                    Tenés <strong>{nConf}</strong> participación(es) por confirmar.
                  </div>
                )}
                {nEval > 0 && (
                  <div>
                    Tenés <strong>{nEval}</strong> evaluación(es) por completar.
                  </div>
                )}
              </div>
              <IonButton size="small" onClick={abrirPendientesCapAdm}>
                Responder ahora
              </IonButton>
            </div>
          )}

          {/* ---------- Historial ---------- */}
          <p className="capadm-card-titulo" style={{ margin: "8px 0 10px" }}>Historial</p>
          {cargando ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
              <CircularProgress />
            </div>
          ) : mis.length === 0 ? (
            <div className="capadm-vacio">
              <IonIcon icon={schoolOutline} />
              Todavía no registraste asistencia a capacitaciones administrativas.
            </div>
          ) : (
            mis.map((c) => (
              <div key={c.id} className="capadm-card" style={{ padding: 14, marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
                  <p className="capadm-card-titulo" style={{ margin: 0 }}>{c.tema}</p>
                  {c.fecha_evaluacion && c.puntaje !== null ? (
                    <span style={{ fontWeight: 700, color: colorPuntaje(Number(c.puntaje)), whiteSpace: "nowrap" }}>
                      {Math.round(Number(c.puntaje))}%
                    </span>
                  ) : c.tiene_evaluacion && c.evaluacion_disponible ? (
                    <IonBadge color="warning" style={{ cursor: "pointer" }} onClick={abrirPendientesCapAdm}>
                      Evaluación pendiente
                    </IonBadge>
                  ) : c.tiene_evaluacion ? (
                    <IonBadge color="medium">En curso</IonBadge>
                  ) : null}
                </div>
                <div className="capadm-meta">
                  <span>
                    <IonIcon icon={calendarOutline} /> {formatFechaCorta(c.fecha_capacitacion)}
                  </span>
                  {c.anfitrion_nombre && (
                    <span>
                      <IonIcon icon={personOutline} /> {c.anfitrion_nombre}
                    </span>
                  )}
                </div>
                {c.tiene_evaluacion && !c.evaluacion_disponible && !c.fecha_evaluacion && (
                  <p className="capadm-card-sub" style={{ margin: "8px 0 0" }}>{textoEvaluacionDesde(c.fecha_fin)}</p>
                )}
              </div>
            ))
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default CapacitacionAdmMis;
